/**
 * @fileoverview Session Times Management Hooks for Admin/Coordinator Role
 * @author Hospital Room Scheduler Team
 * @created 08-10-2025 02:45:00
 * @updated 08-10-2025 16:52:00 - Added planner grid cache invalidation
 * 
 * Enhanced React Query hooks for session time management operations.
 * Provides advanced data fetching, caching, filtering, sorting, and mutation capabilities.
 * 
 * Features:
 * - Advanced filtering with multiple parameters (date range, building, floor, room, status)
 * - Multi-column sorting with ascending/descending order
 * - Server-side pagination with configurable page size
 * - Bulk operations (activate/deactivate, duplicate, delete)
 * - Real-time statistics and analytics
 * - Optimistic updates and intelligent cache invalidation
 * - Comprehensive error handling and loading states
 * - Type-safe filter and sort configurations
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { 
  getRoomSessionTimes,
  getRoomSessionTimeById,
  getRoomSessionTimesByRoom,
  createRoomSessionTime,
  updateRoomSessionTime,
  deleteRoomSessionTime,
  getRoomSessionTimeStats,
  formatSessionDuration,
  getSessionTimeStatusColor,
  type RoomSessionTimeWithDetails,
  type CreateRoomSessionTimeData,
  type UpdateRoomSessionTimeData,
  type RoomSessionTimeStats,
  type GetRoomSessionTimesParams,
} from '../../api/endpoints/session-times';
import { useAuth } from '../useAuth';
import type {
  SessionTimeFilters,
  EnhancedSessionTime,
  BulkOperationData,
  BulkOperationResult,
} from '../../types/session-times';

// =============================================================================
// RE-EXPORTED TYPES FOR CONVENIENCE
// =============================================================================

export type {
  RoomSessionTimeWithDetails,
  CreateRoomSessionTimeData as CreateSessionTimeData,
  UpdateRoomSessionTimeData as UpdateSessionTimeData,
  RoomSessionTimeStats
};

// =============================================================================
// QUERY KEYS
// =============================================================================

export const sessionTimeQueryKeys = {
  /** All session times query key */
  all: ['sessionTimes'] as const,
  
  /** Session times list query key with filters */
  list: (roomId?: string) => ['sessionTimes', 'list', roomId] as const,
  
  /** Session times list with advanced filters */
  listFiltered: (filters?: Partial<SessionTimeFilters>) => 
    ['sessionTimes', 'list', 'filtered', filters] as const,
  
  /** Single session time query key */
  detail: (id: string) => ['sessionTimes', 'detail', id] as const,
  
  /** Room session times query key */
  room: (roomId: string) => ['sessionTimes', 'room', roomId] as const,
  
  /** Session time statistics query key */
  stats: (filters?: { roomId?: string; buildingId?: string }) => 
    ['sessionTimes', 'stats', filters] as const,
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Transform filters to API parameters
 * Converts UI filter state to API query parameters
 * 
 * @param filters - Session time filters
 * @returns API query parameters
 */
function filtersToParams(filters?: Partial<SessionTimeFilters>): GetRoomSessionTimesParams {
  if (!filters) return {};
  
  const params: GetRoomSessionTimesParams = {};
  
  if (filters.page !== undefined) params.page = filters.page;
  if (filters.limit !== undefined) params.limit = filters.limit;
  if (filters.search !== undefined) params.search = filters.search;
  if (filters.roomId !== undefined) params.roomId = filters.roomId;
  if (filters.buildingId !== undefined) params.buildingId = filters.buildingId;
  if (filters.isActive !== undefined) params.isActive = filters.isActive;
  if (filters.sortBy !== undefined && filters.sortBy !== 'bufferMinutes') {
    params.sortBy = filters.sortBy as 'name' | 'startTime' | 'endTime' | 'createdAt' | 'updatedAt';
  }
  if (filters.sortOrder !== undefined) params.sortOrder = filters.sortOrder;
  
  return params;
}

/**
 * Enhance session time with computed UI properties
 * Adds formatted values and metadata for UI display
 * 
 * @param sessionTime - Raw session time data
 * @returns Enhanced session time with UI properties
 */
function enhanceSessionTime(sessionTime: RoomSessionTimeWithDetails): EnhancedSessionTime {
  return {
    ...sessionTime,
    formattedDuration: formatSessionDuration(sessionTime.startTime, sessionTime.endTime),
    statusColor: getSessionTimeStatusColor(sessionTime.isActive),
    buildingName: sessionTime.room.floor.building.name,
    floorName: sessionTime.room.floor.name,
    locationPath: `${sessionTime.room.floor.building.name} > ${sessionTime.room.floor.name} > ${sessionTime.room.name}`,
    searchText: [
      sessionTime.name,
      sessionTime.room.name,
      sessionTime.room.number,
      sessionTime.room.floor.name,
      sessionTime.room.floor.building.name,
    ].join(' ').toLowerCase(),
  };
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Hook to fetch session times with room filtering (original simple version)
 * 
 * @param roomId - Optional room ID to filter by
 * @returns React Query result with session times data
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSessionTimes('room-123');
 * ```
 */
export function useSessionTimes(roomId?: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useQuery({
    queryKey: sessionTimeQueryKeys.list(roomId),
    queryFn: () => getRoomSessionTimes({ ...(roomId && { roomId }) }, token || ''),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Enhanced hook to fetch session times with advanced filtering and sorting
 * Supports comprehensive filtering, pagination, and sorting capabilities
 * 
 * @param filters - Advanced filter configuration
 * @returns React Query result with filtered and sorted session times
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSessionTimesFiltered({
 *   search: 'morning',
 *   buildingId: 'building-123',
 *   isActive: true,
 *   sortBy: 'startTime',
 *   sortOrder: 'asc',
 *   page: 1,
 *   limit: 25
 * });
 * ```
 */
export function useSessionTimesFiltered(filters?: Partial<SessionTimeFilters>) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  const params = useMemo(() => filtersToParams(filters), [filters]);
  
  return useQuery({
    queryKey: sessionTimeQueryKeys.listFiltered(filters),
    queryFn: () => getRoomSessionTimes(params, token || ''),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch and enhance session times with UI-specific properties
 * Returns session times with formatted values and computed metadata
 * 
 * @param filters - Filter configuration
 * @returns Enhanced session times with UI properties
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useEnhancedSessionTimes({
 *   buildingId: 'building-123',
 *   isActive: true
 * });
 * // data.data contains EnhancedSessionTime[] with formattedDuration, locationPath, etc.
 * ```
 */
export function useEnhancedSessionTimes(filters?: Partial<SessionTimeFilters>) {
  const queryResult = useSessionTimesFiltered(filters);
  
  const enhancedData = useMemo(() => {
    if (!queryResult.data) return undefined;
    
    return {
      ...queryResult.data,
      data: queryResult.data.data.map(enhanceSessionTime),
    };
  }, [queryResult.data]);
  
  return {
    ...queryResult,
    data: enhancedData,
  };
}

/**
 * Hook to fetch single session time by ID
 * 
 * @param sessionTemplateId - Session time ID to fetch
 * @returns React Query result with session time data
 * 
 * @example
 * ```tsx
 * const { data: sessionTime, isLoading } = useSessionTime('session-123');
 * ```
 */
export function useSessionTime(sessionTemplateId: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useQuery({
    queryKey: sessionTimeQueryKeys.detail(sessionTemplateId),
    queryFn: () => getRoomSessionTimeById(sessionTemplateId, token || ''),
    enabled: !!sessionTemplateId && !!token,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch session time by ID (alias for useSessionTime)
 * 
 * @param sessionTemplateId - Session time ID to fetch
 * @param enabled - Whether the query should be enabled
 * @returns React Query result with session time data
 * 
 * @example
 * ```tsx
 * const { data: sessionTime, isLoading } = useRoomSessionTimeById('session-123', true);
 * ```
 */
export function useRoomSessionTimeById(sessionTemplateId: string, enabled: boolean = true) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useQuery({
    queryKey: sessionTimeQueryKeys.detail(sessionTemplateId),
    queryFn: () => getRoomSessionTimeById(sessionTemplateId, token || ''),
    enabled: enabled && !!sessionTemplateId && !!token,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch session times for a specific room
 * 
 * @param roomId - Room ID to fetch session times for
 * @returns React Query result with room session times data
 * 
 * @example
 * ```tsx
 * const { data: roomSessionTimes } = useRoomSessionTimes('room-123');
 * ```
 */
export function useRoomSessionTimes(roomId: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useQuery({
    queryKey: sessionTimeQueryKeys.room(roomId),
    queryFn: () => getRoomSessionTimesByRoom(roomId, token || ''),
    enabled: !!roomId && !!token,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch session time statistics
 * 
 * @param filters - Optional filters for statistics
 * @returns React Query result with session time statistics
 * 
 * @example
 * ```tsx
 * const { data: stats } = useRoomSessionTimeStats({ roomId: 'room-123' });
 * ```
 */
export function useRoomSessionTimeStats(filters?: { roomId?: string; buildingId?: string }): UseQueryResult<RoomSessionTimeStats, Error> {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useQuery({
    queryKey: ['sessionTimes', 'stats', filters],
    queryFn: () => getRoomSessionTimeStats(filters || {}, token || ''),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new session time
 * 
 * @returns Mutation for creating session times
 * 
 * @example
 * ```tsx
 * const createSessionTimeMutation = useCreateSessionTime();
 * 
 * const handleCreate = () => {
 *   createSessionTimeMutation.mutate({
 *     name: 'Morning Session',
 *     startTime: '09:00',
 *     endTime: '12:00',
 *     bufferMinutes: 15,
 *     isActive: true,
 *     roomId: 'room-123'
 *   });
 * };
 * ```
 */
export function useCreateSessionTime() {
  const queryClient = useQueryClient();
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useMutation({
    mutationFn: (sessionTimeData: CreateRoomSessionTimeData) => 
      createRoomSessionTime(sessionTimeData, token || ''),
    onSuccess: (data) => {
      // Invalidate session times lists
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.room(data.roomId) });
      
      // CRITICAL FIX: Invalidate planner queries (session times affect grid columns)
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['floorPlan'] });
    },
  });
}

/**
 * Hook to update session time information
 * 
 * @returns Mutation for updating session times
 * 
 * @example
 * ```tsx
 * const updateSessionTimeMutation = useUpdateSessionTime();
 * 
 * const handleUpdate = (sessionTemplateId: string) => {
 *   updateSessionTimeMutation.mutate({
 *     id: sessionTemplateId,
 *     sessionTimeData: { name: 'Updated Session' }
 *   });
 * };
 * ```
 */
export function useUpdateSessionTime() {
  const queryClient = useQueryClient();
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useMutation({
    mutationFn: ({ id, sessionTimeData }: { id: string; sessionTimeData: UpdateRoomSessionTimeData }) => 
      updateRoomSessionTime(id, sessionTimeData, token || ''),
    onSuccess: (data, variables) => {
      // Invalidate specific session time and lists
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.room(data.roomId) });
      
      // CRITICAL FIX: Invalidate planner queries (session time updates affect grid)
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['floorPlan'] });
    },
  });
}

/**
 * Hook to delete session time
 * 
 * @returns Mutation for deleting session times
 * 
 * @example
 * ```tsx
 * const deleteSessionTimeMutation = useDeleteSessionTime();
 * 
 * const handleDelete = (sessionTemplateId: string) => {
 *   deleteSessionTimeMutation.mutate(sessionTemplateId);
 * };
 * ```
 */
export function useDeleteSessionTime() {
  const queryClient = useQueryClient();
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useMutation({
    mutationFn: (sessionTemplateId: string) => deleteRoomSessionTime(sessionTemplateId, token || ''),
    onSuccess: (_, sessionTemplateId) => {
      // Invalidate session times lists
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.detail(sessionTemplateId) });
      
      // CRITICAL FIX: Invalidate planner queries (deleting session time affects grid)
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['floorPlan'] });
    },
  });
}

/**
 * Hook to toggle session time active status
 * Note: Implemented using update mutation since there's no dedicated toggle endpoint
 * 
 * @returns Mutation for toggling session time status
 * 
 * @example
 * ```tsx
 * const toggleStatusMutation = useToggleSessionTimeStatus();
 * 
 * const handleToggle = async (sessionTime: RoomSessionTimeWithDetails) => {
 *   toggleStatusMutation.mutate({
 *     id: sessionTime.id,
 *     currentStatus: sessionTime.isActive
 *   });
 * };
 * ```
 */
export function useToggleSessionTimeStatus() {
  const queryClient = useQueryClient();
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useMutation({
    mutationFn: ({ id, currentStatus }: { id: string; currentStatus: boolean }) => 
      updateRoomSessionTime(id, { isActive: !currentStatus }, token || ''),
    onSuccess: (data, variables) => {
      // Invalidate specific session time and lists
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.room(data.roomId) });
      
      // CRITICAL FIX: Invalidate planner queries (status toggle affects grid)
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['floorPlan'] });
    },
  });
}

// =============================================================================
// BULK OPERATION HOOKS
// =============================================================================

/**
 * Hook to perform bulk operations on session times
 * Supports activate, deactivate, duplicate, and delete operations
 * 
 * @returns Mutation for bulk operations
 * 
 * @example
 * ```tsx
 * const bulkOperationMutation = useBulkSessionTimeOperation();
 * 
 * const handleBulkActivate = (selectedIds: string[]) => {
 *   bulkOperationMutation.mutate({
 *     type: 'activate',
 *     sessionTemplateIds: selectedIds
 *   });
 * };
 * ```
 */
export function useBulkSessionTimeOperation() {
  const queryClient = useQueryClient();
  const { getStoredToken } = useAuth();
  const token = getStoredToken();
  
  return useMutation({
    mutationFn: async (operation: BulkOperationData): Promise<BulkOperationResult> => {
      const results: BulkOperationResult = {
        success: true,
        successCount: 0,
        failureCount: 0,
        errors: [],
      };
      
      // Execute bulk operation based on type
      for (const sessionTemplateId of operation.sessionTemplateIds) {
        try {
          switch (operation.type) {
            case 'activate':
              await updateRoomSessionTime(sessionTemplateId, { isActive: true }, token || '');
              break;
            case 'deactivate':
              await updateRoomSessionTime(sessionTemplateId, { isActive: false }, token || '');
              break;
            case 'delete':
              await deleteRoomSessionTime(sessionTemplateId, token || '');
              break;
            case 'duplicate':
              // Note: Duplicate requires fetching the session time first
              const sessionTime = await getRoomSessionTimeById(sessionTemplateId, token || '');
              await createRoomSessionTime({
                roomId: sessionTime.roomId,
                name: `${sessionTime.name} (Copy)`,
                startTime: sessionTime.startTime,
                endTime: sessionTime.endTime,
                bufferMinutes: sessionTime.bufferMinutes,
                isActive: false, // Duplicates start as inactive
              }, token || '');
              break;
            default:
              throw new Error(`Unsupported bulk operation type: ${operation.type}`);
          }
          results.successCount++;
        } catch (error) {
          results.failureCount++;
          results.errors?.push({
            sessionTemplateId,
            message: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      }
      
      results.success = results.failureCount === 0;
      return results;
    },
    onSuccess: () => {
      // Invalidate all session time queries
      queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.all });
      
      // CRITICAL FIX: Invalidate planner queries (bulk operations affect grid)
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['floorPlan'] });
    },
  });
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to invalidate all session time queries
 * Useful for manual cache invalidation after external updates
 * 
 * @returns Callback to invalidate session time queries
 * 
 * @example
 * ```tsx
 * const invalidateSessionTimes = useInvalidateSessionTimeQueries();
 * 
 * const handleExternalUpdate = () => {
 *   invalidateSessionTimes();
 * };
 * ```
 */
export function useInvalidateSessionTimeQueries() {
  const queryClient = useQueryClient();
  
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: sessionTimeQueryKeys.all });
  }, [queryClient]);
}